import { initializeApp, getApp, getApps } from 'firebase/app';
import { Capacitor } from '@capacitor/core';
import {
  getAuth, initializeAuth, indexedDBLocalPersistence, browserLocalPersistence,
  browserSessionPersistence, connectAuthEmulator,
} from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

const projectId = process.env.REACT_APP_INSPECTION_FIREBASE_PROJECT_ID;
const apiKey = process.env.REACT_APP_INSPECTION_FIREBASE_API_KEY;
const authDomain = process.env.REACT_APP_INSPECTION_FIREBASE_AUTH_DOMAIN;
const appId = process.env.REACT_APP_INSPECTION_FIREBASE_APP_ID;
const messagingSenderId = process.env.REACT_APP_INSPECTION_FIREBASE_MESSAGING_SENDER_ID;
const storageBucket = process.env.REACT_APP_INSPECTION_FIREBASE_STORAGE_BUCKET;
const region = process.env.REACT_APP_INSPECTION_FUNCTIONS_REGION || 'us-central1';
const useEmulators = process.env.REACT_APP_INSPECTION_USE_EMULATORS === 'true';
const expectedProjectId = useEmulators ? 'demo-fleet-inspections' : 'logistixnerd-fleet';

const hasFirebaseConfig = [projectId, apiKey, authDomain, appId, messagingSenderId, storageBucket]
  .every((value) => typeof value === 'string' && value.trim().length > 0);
export const inspectionFirebaseConfigured = hasFirebaseConfig && projectId === expectedProjectId;
export const inspectionFirebaseConfigError = !hasFirebaseConfig
  ? 'Fleet Firebase configuration is missing. Configure the REACT_APP_INSPECTION_FIREBASE_* environment variables.'
  : projectId !== expectedProjectId
    ? `Fleet Firebase must use ${expectedProjectId}${useEmulators ? ' when emulators are enabled' : ''}.`
    : '';

let app = null;
let auth = null;
let functions = null;
let functionsBaseUrl = '';
let initializationError = null;

if (inspectionFirebaseConfigured) {
  try {
    const config = { projectId, apiKey, authDomain, appId, messagingSenderId, storageBucket };
    app = getApps().some((candidate) => candidate.name === 'inspection-project')
      ? getApp('inspection-project')
      : initializeApp(config, 'inspection-project');
    auth = Capacitor.isNativePlatform()
      ? initializeAuth(app, {
        persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
      })
      : getAuth(app);
    // Firebase 12.6 authStateReady/onAuthStateChanged do not forward initialization rejections.
    auth._initializationPromise.catch((error) => {
      initializationError = error;
      console.error('Fleet authentication initialization failed:', error);
    });
    functions = getFunctions(app, region);

    if (useEmulators) {
      const emulatorHost = process.env.REACT_APP_INSPECTION_EMULATOR_HOST || '127.0.0.1';
      const authPort = Number(process.env.REACT_APP_INSPECTION_AUTH_EMULATOR_PORT || 9099);
      const functionsPort = Number(process.env.REACT_APP_INSPECTION_FUNCTIONS_EMULATOR_PORT || 5001);
      connectAuthEmulator(auth, `http://${emulatorHost}:${authPort}`, { disableWarnings: true });
      connectFunctionsEmulator(functions, emulatorHost, functionsPort);
      functionsBaseUrl = `http://${emulatorHost}:${functionsPort}/${projectId}/${region}`;
    } else {
      functionsBaseUrl = `https://${region}-${projectId}.cloudfunctions.net`;
    }
  } catch (error) {
    initializationError = error;
    console.error('Fleet Firebase initialization failed:', error);
  }
}

export function waitForInspectionAuthInitialization() {
  if (initializationError) return Promise.reject(initializationError);
  if (!auth) return Promise.reject(new Error(inspectionFirebaseConfigError || 'Fleet authentication is unavailable.'));
  return auth._initializationPromise;
}

export function retryInspectionAuthentication() {
  // Restart a rejected/stalled SDK instance without deleting persisted sessions or offline drafts.
  window.location.reload();
}

export { app as inspectionApp, auth as inspectionAuth, functions as inspectionFunctions, functionsBaseUrl };
